import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { SITE_URL } from '@/components/base/SEO';

export default function SafetyGuide() {
  return (
    <main>
      <SEO
        title="Is Litbuy Spreadsheet Safe? Security & Privacy Guide | LitbuySpreadsheets.online"
        description="Honest security analysis of Litbuy Spreadsheet covering data protection, local storage, privacy practices, and best practices for keeping your business data secure."
        canonicalPath="/litbuy-spreadsheet-safety"
        keywords="litbuy spreadsheet safety, litbuy spreadsheet security, spreadsheet data protection, e-commerce data privacy, secure spreadsheet tools"
        schemas={[
          {
            '@type': 'Article',
            headline: 'Is Litbuy Spreadsheet Safe? Security and Privacy Guide',
            description: 'An honest security analysis covering data protection, local storage options, privacy practices, and best practices for keeping your business data secure with Litbuy Spreadsheet.',
            author: { '@type': 'Organization', name: 'Litbuy Editorial Team' },
            publisher: { '@type': 'Organization', name: 'Litbuy Spreadsheets', url: SITE_URL },
            datePublished: '2026-06-15',
            dateModified: '2026-06-20',
            mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/litbuy-spreadsheet-safety` },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Is Litbuy Spreadsheet Safe?', item: `${SITE_URL}/litbuy-spreadsheet-safety` },
            ],
          },
        ]}
      />
      <Navbar />
      <article>
        <section className="relative pt-24 md:pt-28 pb-16 md:pb-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">Is Litbuy Spreadsheet Safe?</span>
            </nav>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Safety</span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
              Is Litbuy Spreadsheet Safe? Security and Privacy Guide
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              An honest security analysis covering data protection, local storage options, privacy practices, and best practices for keeping your business data secure with Litbuy Spreadsheet.
            </p>
          </div>
        </section>

        <section className="relative py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto space-y-14">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Data Security That Sellers Can Trust</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">When managing product inventories and financial data, security is non-negotiable. Litbuy Spreadsheet handles your business information with multiple layers of protection. Your spreadsheets are stored securely with encryption both at rest and in transit.</p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">Unlike some cloud-based tools that share data with third parties for advertising purposes, Litbuy Spreadsheet maintains strict data isolation. Your catalog, sales figures, and supplier information remain private and accessible only to you and your authorized team members.</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3"><i className="ri-shield-check-line text-lg"></i></div>
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">Encrypted Storage</h4>
                  <p className="text-xs text-foreground-600">Data encrypted at rest and in transit</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3"><i className="ri-lock-line text-lg"></i></div>
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">No Data Sharing</h4>
                  <p className="text-xs text-foreground-600">Your data is never sold or shared</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3"><i className="ri-user-settings-line text-lg"></i></div>
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">Access Control</h4>
                  <p className="text-xs text-foreground-600">Granular permissions for team members</p>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-10 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">Try Litbuy Spreadsheet Safely</h2>
              <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">Start with confidence on the official store.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors">Visit Main Store <i className="ri-arrow-right-up-line"></i></a>
                <Link to="/" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors">Back to Home <i className="ri-home-4-line"></i></Link>
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-8">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">Continue Reading</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/litbuy-spreadsheet-guide" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Complete Guide</Link>
                <Link to="/litbuy-spreadsheet-pros-cons" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Pros and Cons</Link>
                <Link to="/" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Home</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </main>
  );
}